import {
  ActivityProgress,
  CompanyId,
  CustomType,
  GoalWithProgress,
  ProgressType,
  UserCompanyDto,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
} from '../../services/api.types'
import { msgid, ngettext, t } from 'ttag'
import { unitsCountDurationMinuteLong, unitsCountDurationMinuteShort, unitsCountEnergyCalorieLong } from './units'
import { CustomTypeCategory, getCustomUnitText } from '../../services/CustomTypes'
import moment from 'moment'
import { getPeriodString } from './date'
import * as React from 'react'
import { endOfDay, isValid, parseISO } from 'date-fns'
import { yuMuuvPublicCompanyId } from '../../views/Settings/Settings'
import { red } from '../../styles/material-ui-theme-v2'

export const isTotalType = (progressType: ProgressType) => {
  return [ProgressType.ALL, ProgressType.STEPS].includes(progressType)
}

export const isStepsType = (progressType: ProgressType) =>
  [ProgressType.STEPS, ProgressType.COLLECTIVE_STEPS, ProgressType.PERSISTENCE_STEPS].includes(progressType)

/*export const isManualEntryType = (progressType: ProgressType) =>
  [
    ProgressType.CUSTOM,
    ProgressType.COLLECTIVE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)*/

export const isPersistenceType = (progressType: ProgressType) => {
  return [
    ProgressType.PERSISTENCE_STEPS,
    ProgressType.PERSISTENCE_CALORIES,
    ProgressType.PERSISTENCE_INTENSIVE_MINUTES,
    ProgressType.PERSISTENCE_ALL,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)
}

export const isCollectiveType = (progressType: ProgressType) => {
  return [
    ProgressType.COLLECTIVE_STEPS,
    ProgressType.COLLECTIVE_CALORIES,
    ProgressType.COLLECTIVE_INTENSIVE_MINUTES,
    ProgressType.COLLECTIVE_CUSTOM,
  ].includes(progressType)
}

export const isSimpleDayType = (activityType: CustomTypeCategory) => {
  return [
    CustomTypeCategory.CAR_FREE_DAYS,
    CustomTypeCategory.DAILY_FLOSSING,
    CustomTypeCategory.ALCOHOL_ABSTINENCE,
    CustomTypeCategory.FINANCE_TRACKING,
    CustomTypeCategory.DAILY_JOURNALING,
    CustomTypeCategory.GRATITUDE_JOURNALING,
    CustomTypeCategory.NATURAL_LIGHT_EXPOSURE,
    CustomTypeCategory.SMOKING_CESSATION,
  ].includes(activityType)
}

export const isActivityIndexType = (progressType: ProgressType) => {
  return [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)
}

export const isPointsType = (progressType: ProgressType) => {
  return [ProgressType.POINTS, ProgressType.PERSISTENCE_POINTS, ProgressType.COLLECTIVE_POINTS].includes(progressType)
}

export const isManualEntryOnlyType = (progressType: ProgressType, customType: CustomType | null | undefined) => {
  //return progressType === ProgressType.CUSTOM && !isDistanceType(customType)
  return progressType === ProgressType.CUSTOM && !isAutomatedType(customType)
}

export const isCustomType = (progressType: ProgressType) => {
  return [
    ProgressType.CUSTOM,
    ProgressType.COLLECTIVE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM,
    ProgressType.PERSISTENCE_CUSTOM_BINARY,
  ].includes(progressType)
}

export const isDistanceType = (customType: CustomType | null | undefined) => {
  return !!(
    customType &&
    [
      CustomType.DISTANCE_KM,
      CustomType.DISTANCE_MI,
      CustomType.CYCLING_KM,
      CustomType.CYCLING_MI,
      CustomType.RUNNING_KM,
      CustomType.RUNNING_MI,
      CustomType.WALKING_KM,
      CustomType.WALKING_MI,
    ].includes(customType)
  )
}

export const isAutomatedType = (customType: CustomType | null | undefined) => {
  return !!(
    customType &&
    [
      CustomType.STEPS,
      CustomType.CALORIES,
      CustomType.INTENSIVE_MINUTES,
      CustomType.ACTIVITY_INDEX,
      CustomType.CYCLING_KM,
      CustomType.CYCLING_MI,
      CustomType.CYCLING_HR,
      CustomType.CYCLING_MIN,
      CustomType.DISTANCE_KM,
      CustomType.DISTANCE_MI,
      CustomType.EXERCISE_MIN,
      CustomType.EXERCISE_HR,
      // CustomType.MINDFULNESS_MIN,
      // CustomType.MINDFULNESS_HR,
      // CustomType.OUTDOOR_TIME_MIN,
      // CustomType.OUTDOOR_TIME_HR,
      // CustomType.PLANKING_MIN,
      // CustomType.PLANKING_HR,
      CustomType.RUNNING_KM,
      CustomType.RUNNING_MI,
      CustomType.RUNNING_MIN,
      CustomType.RUNNING_HR,
      // CustomType.SLEEP_HR,
      // CustomType.SLEEP_MIN,
      CustomType.SWIMMING_M,
      CustomType.SWIMMING_YD,
      CustomType.SWIMMING_KM,
      CustomType.SWIMMING_MI,
      CustomType.SWIMMING_MIN,
      CustomType.SWIMMING_HR,
      CustomType.WALKING_KM,
      CustomType.WALKING_MI,
      CustomType.WALKING_MIN,
      CustomType.WALKING_HR,
      // CustomType.WATER_DRINKING_GLASS,
      // CustomType.WATER_DRINKING_ML,
      // CustomType.WATER_DRINKING_CUP,
      // CustomType.WATER_DRINKING_OZ,
      CustomType.YOGA_MIN,
      CustomType.YOGA_HR,
      CustomType.POINTS,
    ].includes(customType)
  )
}

export const getChallengeProgressCurrently = (goal: GoalWithProgress, activityProgress: ActivityProgress) => {
  const { progressType, collectiveTotal } = goal
  if (isCollectiveType(progressType)) return collectiveTotal
  if (!activityProgress) return 0
  const { totalPercentage, persistenceTotal, stepsTotal, intensiveMinutesTotal } = activityProgress
  switch (progressType) {
    case ProgressType.ALL:
      return totalPercentage
    case ProgressType.STEPS:
      return stepsTotal
    case ProgressType.INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceTotal
    default:
      return -1
  }
}

export const getChallengePersonalProgressCurrently = (goal: GoalWithProgress, activityProgress: ActivityProgress) => {
  const { progressType } = goal
  if (!activityProgress) return 0
  const { totalPercentage, persistenceTotal, caloriesTotal, stepsTotal, intensiveMinutesTotal } = activityProgress
  switch (progressType) {
    case ProgressType.ALL:
      return totalPercentage
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return stepsTotal
    case ProgressType.COLLECTIVE_CALORIES:
      return caloriesTotal
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceTotal
    default:
      return -1
  }
}

export const getChallengeDailyProgressCurrently = (
  progressType: ProgressType,
  dailyDiary: UserDailyDiaryProgress[],
  progressResults: UserDailyProgress[],
  customProgress: UserCustomDailyProgress[],
  day: string,
  manualEntryEnabled: boolean,
) => {
  // Points
  if (progressType === ProgressType.PERSISTENCE_CUSTOM && customProgress) {
    const d = customProgress?.find((item) => item.date === day)
    return !d ? 0 : d.value
  }
  const isActivityIndex = isActivityIndexType(progressType)
  if (!isActivityIndex) {
    const d = dailyDiary?.find((item) => item.date === day)
    return !d ? 0 : manualEntryEnabled ? d.sum : d.integrationValue
  } else {
    const todayResults = progressResults.find((item) => item.date === day)
    return todayResults ? getPersistenceDailyTotal(todayResults, progressType, manualEntryEnabled) : 0
  }
}

export const getChallengeProgressGoal = (goal: GoalWithProgress, activityProgress: ActivityProgress): number => {
  const { progressType, activityFormulaTarget, customGoal, stepsGoal, persistenceGoal, collectiveGoal } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return activityFormulaTarget || 100
    case ProgressType.STEPS:
      return stepsGoal
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.COLLECTIVE_CALORIES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return collectiveGoal || 0
    case ProgressType.CUSTOM:
    case ProgressType.INTENSIVE_MINUTES:
      return customGoal || 0
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return persistenceGoal || 0
    default:
      return -1
  }
}

export const getChallengeProgressUnits = (goal: GoalWithProgress): string => {
  const { progressType, customType, customUnit, persistenceGoal } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return '%'
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return t`steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return unitsCountEnergyCalorieLong
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return unitsCountDurationMinuteShort
    //return unitsCountDurationMinuteLong
    case ProgressType.CUSTOM:
    case ProgressType.COLLECTIVE_CUSTOM:
      return getCustomUnitText(customType, customType, customUnit) as string
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return ngettext(msgid`day`, `days`, persistenceGoal || 0)
    default:
      return ''
  }
}

export const getChallengeGoalUnits = (goal: GoalWithProgress): string => {
  const { progressType, customType, customUnit } = goal
  switch (progressType) {
    case ProgressType.ALL:
    case ProgressType.PERSISTENCE_ALL:
      return '%'
    case ProgressType.STEPS:
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.PERSISTENCE_STEPS:
      return t`steps`
    case ProgressType.COLLECTIVE_CALORIES:
      return unitsCountEnergyCalorieLong
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
      return unitsCountDurationMinuteShort
    //return unitsCountDurationMinuteLong
    case ProgressType.CUSTOM:
    case ProgressType.COLLECTIVE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM:
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
      return getCustomUnitText(customType, customType, customUnit) as string
    default:
      return ''
  }
}

export const getPersistenceDailyTotal = (
  userDailyProgress: UserDailyProgress,
  progressType: ProgressType,
  manualEntryEnabled?: boolean,
) => {
  const { stepsTotal, caloriesTotal, intensiveMinutesTotal, totalPercentage, integrationTotalPercentage } =
    userDailyProgress
  switch (progressType) {
    case ProgressType.STEPS:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.COLLECTIVE_STEPS:
      return stepsTotal
    case ProgressType.CALORIES:
    case ProgressType.PERSISTENCE_CALORIES:
    case ProgressType.COLLECTIVE_CALORIES:
      return caloriesTotal
    case ProgressType.INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
      return intensiveMinutesTotal
    case ProgressType.ALL:
    case ProgressType.PERSISTENCE_ALL:
      return manualEntryEnabled ? totalPercentage : integrationTotalPercentage
    default:
      return 0
  }
}

export const getDailyValueGoal = (goal: GoalWithProgress, progressType: ProgressType): number => {
  const { dailyPersistenceGoal, dailyStepsGoal, activityFormulaTarget } = goal
  switch (progressType) {
    case ProgressType.ALL:
      return activityFormulaTarget || 100
    case ProgressType.STEPS:
      return dailyStepsGoal || 12000
    case ProgressType.INTENSIVE_MINUTES:
      return 20
    case ProgressType.CUSTOM:
    case ProgressType.PERSISTENCE_ALL:
    case ProgressType.PERSISTENCE_STEPS:
    case ProgressType.PERSISTENCE_CALORIES:
    case ProgressType.PERSISTENCE_INTENSIVE_MINUTES:
    case ProgressType.PERSISTENCE_CUSTOM:
      return dailyPersistenceGoal || 1
    case ProgressType.COLLECTIVE_STEPS:
    case ProgressType.COLLECTIVE_CALORIES:
    case ProgressType.COLLECTIVE_INTENSIVE_MINUTES:
    case ProgressType.COLLECTIVE_CUSTOM:
      // Hard to choose a daily goal for custom, just set it large number.
      return 999999999
    case ProgressType.PERSISTENCE_CUSTOM_BINARY:
    default:
      return 1
  }
}

const getMasterUserCompany = (userCompanies: UserCompanyDto[], iCompanyId: CompanyId) => {
  let companyId = iCompanyId
  if (companyId === 'new') {
    const ownerCompanies = userCompanies
      .filter(
        ({ companyId, ownerCompanyId, userGoalsEnabled }) =>
          companyId != yuMuuvPublicCompanyId && !ownerCompanyId && userGoalsEnabled,
      )
      .sort((a, b) => {
        // Compare the dates to sort in descending order.
        return a.createdAt - b.createdAt
      })
    if (ownerCompanies[0]?.companyId) {
      companyId = ownerCompanies[0].companyId
    }
  }
  let userCompany = userCompanies.find((uc) => uc.companyId === companyId)
  if (!userCompany) return null
  // Allow case if we have manually set period for user created company.
  if (userCompany.startAt || userCompany.paidUntil) return userCompany
  // Otherwise return master company.
  if (userCompany.ownerCompanyId) {
    const ownerCompanyId = userCompany.ownerCompanyId
    userCompany = userCompanies.find((uc) => uc.companyId === ownerCompanyId)
  }
  return userCompany
}

export const getAccountPlan = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany?.plan) return null
  return userCompany.plan
}

export const getAccountStartDate = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const dateString = userCompany.plan === 'trial' ? userCompany.trialStartDate : userCompany.startAt
  const date = parseISO(dateString)
  return isValid(date) ? date : null
}

export const getAccountEndDate = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const dateString = userCompany.plan === 'trial' ? userCompany.trialEndDate : userCompany.paidUntil
  const date = parseISO(dateString)
  return isValid(date) ? date : null
}

export const getContractPeriod = (userCompanies: UserCompanyDto[], companyId: CompanyId) => {
  if (!userCompanies || !companyId) return null
  const userCompany = getMasterUserCompany(userCompanies, companyId)
  if (!userCompany) return null
  const startDateString = userCompany.plan === 'trial' ? userCompany.trialStartDate : userCompany.startAt
  const endDateString = userCompany.plan === 'trial' ? userCompany.trialEndDate : userCompany.paidUntil
  const planStartDate = parseISO(startDateString).getTime()
  const planEndDate = parseISO(endDateString).getTime()
  const contractPeriod = planStartDate && planEndDate ? getPeriodString(planStartDate, planEndDate) : null
  if (!contractPeriod) return null
  const hasEnded = endOfDay(parseISO(endDateString)).getTime() < new Date().getTime()
  return (
    <>
      {userCompany.plan === 'trial' ? t`Trial period` : t`Contract period`}:{' '}
      <strong style={hasEnded ? { color: red } : {}}>{contractPeriod}</strong>
    </>
  )
}
